import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { HubspotFormData } from '@/interfaces';
import HeroSection from './components/HeroSection';
import Eyebrow from './components/Eyebrow';
import Headline from './components/Headline';
import Subheadline from './components/Subheadline';
import Description from './components/Description';
import FormCard from './components/FormCard';

interface ImageLeftFormRightProps {
  slice: Content.LeadCaptureHeroSlice;
  /**
   * HubSpot form field schema, fetched server-side in the dispatcher. `null`
   * when no `form_id` is configured on the slice.
   */
  formData: HubspotFormData | null;
}

/**
 * "Image Left, Form Right" variation for `LeadCaptureHero`.
 *
 * Owns only the layout decisions for this variation (desktop two-column grid:
 * image left spanning both rows, copy top-right, form bottom-right; mobile
 * stacks copy → image → form). Every shared piece — the section shell,
 * eyebrow, headline/subheadline, description, and the form card — comes from
 * `./components/*` so the variation stays independent of the other variants
 * while reusing the same visual and submission spec.
 */
const ImageLeftFormRight: FC<ImageLeftFormRightProps> = ({
  slice,
  formData,
}) => {
  const primary =
    slice.primary as Content.LeadCaptureHeroSliceImageLeftFormRightPrimary;
  const {
    background,
    eyebrow,
    headline,
    subheadline,
    description,
    form_title,
    resource_image,
    footnote,
    form_id,
    form_position,
    form_field_style,
    form_field_layout,
    success_content,
    redirect_link,
  } = primary;

  // Default is form on the right (image left, copy+form right). "Left" swaps
  // columns on desktop only — mobile source order (copy → image → form) is
  // preserved so the hero still reads top to bottom.
  const formOnLeft = (form_position ?? 'Right').toLowerCase() === 'left';
  const copyColClass = formOnLeft
    ? 'lg:col-start-1 lg:row-start-1'
    : 'lg:col-start-2 lg:row-start-1';
  const imageColClass = formOnLeft
    ? 'lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center'
    : 'lg:col-start-1 lg:row-start-1 lg:row-span-2 lg:self-center';
  const formColClass = formOnLeft
    ? 'lg:col-start-1 lg:row-start-2'
    : 'lg:col-start-2 lg:row-start-2';

  return (
    <HeroSection
      sliceType={slice.slice_type}
      variation={slice.variation}
      backgroundImage={background}
    >
      <div className="relative z-10 mx-auto grid max-w-[1280px] grid-cols-1 gap-y-10 lg:grid-cols-2 lg:items-start lg:gap-x-[60px] lg:gap-y-6">
        {/* Copy — mobile 1st, desktop column driven by form_position, top row */}
        <div className={`flex w-full flex-col gap-2 ${copyColClass}`}>
          <Eyebrow text={eyebrow} />
          <div className="flex flex-col text-center lg:text-left">
            <Headline field={headline} />
            <Subheadline field={subheadline} />
          </div>
          <Description
            field={description}
            className="font-inter text-[16px] font-normal leading-[1.5] text-white/80 md:text-[18px]"
          />
        </div>

        {/* Image — mobile 2nd, desktop opposite side from the form, spanning
            both rows */}
        {isFilled.image(resource_image) && (
          <div className={`w-full ${imageColClass}`}>
            <PrismicNextImage
              field={resource_image}
              className="h-auto w-full object-contain"
              alt=""
            />
          </div>
        )}

        {/* Form card — mobile 3rd, desktop column driven by form_position,
            bottom row */}
        <FormCard
          formData={formData}
          formTitle={form_title}
          formId={form_id}
          footnote={footnote}
          formFieldStyle={form_field_style}
          formFieldLayout={form_field_layout}
          successContent={success_content}
          redirectLink={redirect_link}
          className={formColClass}
        />
      </div>
    </HeroSection>
  );
};

export default ImageLeftFormRight;
